import { ApolloError } from '@apollo/client';
import { PERMISSION_ERROR_MESSAGE } from 'utils/apollo/errorHandling';

export const CONTACT_ADMINISTRATOR_PERMISSION_MESSAGE = 'Contact your administrator if you believe this is an error.';

export const REGIONS_VIEW_PERMISSION_MESSAGE = `You do not have permission to view Regions. ${CONTACT_ADMINISTRATOR_PERMISSION_MESSAGE}`;
export const REGIONS_ACTION_PERMISSION_MESSAGE = `You do not have permission to perform this Regions action. ${CONTACT_ADMINISTRATOR_PERMISSION_MESSAGE}`;
export const REGIONS_RELATED_OPTIONS_PERMISSION_MESSAGE = `You do not have permission to load related region data. ${CONTACT_ADMINISTRATOR_PERMISSION_MESSAGE}`;
export const REGION_NOT_FOUND_MESSAGE = 'Region not found.';

const AUTHORIZATION_MESSAGE_PATTERN =
  /\b(?:not\s+authorized|forbidden|permission\s+denied|insufficient\s+permissions|unauthori[sz]ed)\b/i;
const PERMISSION_CODES = new Set(['UNAUTHORIZED', 'FORBIDDEN']);
const PERMISSION_STATUS_CODES = new Set([403]);

const asMessage = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

const isDefinedString = (value: string | undefined): value is string => Boolean(value);
const hasKnownPermissionCode = (value: unknown): boolean =>
  typeof value === 'string' && PERMISSION_CODES.has(value.trim().toUpperCase());
const hasKnownPermissionStatus = (value: unknown): boolean =>
  typeof value === 'number' && PERMISSION_STATUS_CODES.has(value);
const asObject = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : null;

const hasPermissionCode = (errorLike: unknown): boolean => {
  if (Array.isArray(errorLike)) {
    return errorLike.some((item) => {
      const errorObject = asObject(item);
      if (!errorObject) {
        return false;
      }
      return hasKnownPermissionCode(errorObject.code) || hasKnownPermissionCode(asObject(errorObject.extensions)?.code);
    });
  }

  if (errorLike instanceof ApolloError) {
    return errorLike.graphQLErrors.some((error) => hasKnownPermissionCode(error.extensions?.code));
  }

  if (errorLike && typeof errorLike === 'object') {
    const maybeErrorWithCodes = errorLike as {
      code?: unknown;
      errors?: unknown;
    };

    if (hasKnownPermissionCode(maybeErrorWithCodes.code)) {
      return true;
    }

    if (
      Array.isArray(maybeErrorWithCodes.errors) &&
      maybeErrorWithCodes.errors.some((error) => {
        const nestedErrorObject = asObject(error);
        return (
          hasKnownPermissionCode(nestedErrorObject?.code) ||
          hasKnownPermissionCode(asObject(nestedErrorObject?.extensions)?.code)
        );
      })
    ) {
      return true;
    }
  }

  return false;
};

const hasPermissionStatusCode = (errorLike: unknown): boolean => {
  if (errorLike instanceof ApolloError) {
    const networkError = asObject(errorLike.networkError);

    return hasKnownPermissionStatus(networkError?.statusCode) || hasKnownPermissionStatus(networkError?.status);
  }

  const errorObject = asObject(errorLike);
  if (!errorObject) {
    return false;
  }

  const networkError = asObject(errorObject.networkError);

  return (
    hasKnownPermissionStatus(errorObject.statusCode) ||
    hasKnownPermissionStatus(errorObject.status) ||
    hasKnownPermissionStatus(networkError?.statusCode) ||
    hasKnownPermissionStatus(networkError?.status)
  );
};

const toErrorMessages = (errorLike: unknown): string[] => {
  const directMessage = asMessage(errorLike);
  if (directMessage) return [directMessage];

  if (Array.isArray(errorLike)) {
    return errorLike
      .map((item) =>
        item && typeof item === 'object' ? asMessage((item as { message?: unknown }).message) : undefined
      )
      .filter(isDefinedString);
  }

  if (errorLike instanceof ApolloError) {
    return [
      ...errorLike.graphQLErrors.map((error) => asMessage(error.message)),
      errorLike.networkError instanceof Error ? asMessage(errorLike.networkError.message) : undefined,
      asMessage(errorLike.message),
    ].filter(isDefinedString);
  }

  if (errorLike instanceof Error) {
    return [asMessage(errorLike.message)].filter(isDefinedString);
  }

  if (errorLike && typeof errorLike === 'object') {
    const maybeErrorWithMessages = errorLike as {
      message?: unknown;
      errors?: unknown;
    };

    const nestedMessages = Array.isArray(maybeErrorWithMessages.errors)
      ? maybeErrorWithMessages.errors.map((error) => asMessage(asObject(error)?.message))
      : [];

    return [...nestedMessages, asMessage(maybeErrorWithMessages.message)].filter(isDefinedString);
  }

  return [];
};

export const isPermissionError = (errorLike: unknown): boolean => {
  if (hasPermissionCode(errorLike) || hasPermissionStatusCode(errorLike)) {
    return true;
  }

  const messages = toErrorMessages(errorLike);

  return messages.some(
    (message) => message === PERMISSION_ERROR_MESSAGE || AUTHORIZATION_MESSAGE_PATTERN.test(message)
  );
};

export const getRegionsFailureDescription = (
  errorLike: unknown,
  fallbackDescription: string,
  permissionMessage: string = REGIONS_ACTION_PERMISSION_MESSAGE
): string => {
  return isPermissionError(errorLike) ? permissionMessage : fallbackDescription;
};

type RegionsFailureNotification = {
  description: string;
  status: 'warning' | 'error';
};

export const getRegionsFailureNotification = (
  errorLike: unknown,
  fallbackDescription: string,
  permissionMessage: string = REGIONS_ACTION_PERMISSION_MESSAGE
): RegionsFailureNotification => {
  const isPermissionFailure = isPermissionError(errorLike);

  return {
    description: isPermissionFailure ? permissionMessage : fallbackDescription,
    status: isPermissionFailure ? 'warning' : 'error',
  };
};
